import { Button, Flex, Icon, IconName, Text, useTheme } from "@repo/ui";
import { unreachable } from "@repo/utils/typeUtils";
import { useStyles } from "purse-styles";
import { useEntity } from "../db/DatabaseProvider";
import { PatientId, TaskId } from "../db/schema";

export type ContextChip =
	| {
			type: "task";
			id: TaskId;
	  }
	| {
			type: "patient";
			id: PatientId;
	  };

function ContextChipTemplate({
	icon,
	text,
	onClose,
}: {
	icon: IconName;
	text: string;
	onClose?: () => void;
}) {
	const buttonClassName = useStyles({
		visibility: "hidden",
		[".context-chip:hover &"]: {
			visibility: "visible",
		},
	});

	return (
		<Flex
			background="sand2"
			borderRadius="sm"
			inline
			align="center"
			className="context-chip"
			shrink={0}
		>
			<Flex
				gap="xxs"
				align="center"
				padding="xxs"
				style={{ maxWidth: "200px", minWidth: 0 }}
			>
				<Icon name={icon} size="xs" color="sand11" shrink={0} />
				<Text size="xs" color="sand11" truncate>
					{text}
				</Text>
			</Flex>
			<Button
				variant="ghost"
				size="xxs"
				onPress={onClose}
				shrink={0}
				className={buttonClassName}
				style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
			>
				<Icon name="X" size="xs" />
			</Button>
		</Flex>
	);
}

function TaskContextChip({
	taskId,
	onClose,
}: {
	taskId: TaskId;
	onClose?: () => void;
}) {
	const task = useEntity("task", taskId);
	const taskName = task?.title || "Unknown Task";

	return (
		<ContextChipTemplate icon="FileText" text={taskName} onClose={onClose} />
	);
}

function PatientContextChip({
	patientId,
	onClose,
}: {
	patientId: PatientId;
	onClose?: () => void;
}) {
	const patient = useEntity("patient", patientId);
	const patientName = patient?.name || "Unknown Patient";

	return (
		<ContextChipTemplate
			icon="CircleUser"
			text={patientName}
			onClose={onClose}
		/>
	);
}

export function ContextChipView({
	chip,
	onClose,
}: {
	chip: ContextChip;
	onClose?: () => void;
}) {
	switch (chip.type) {
		case "task":
			return <TaskContextChip taskId={chip.id} onClose={onClose} />;
		case "patient":
			return <PatientContextChip patientId={chip.id} onClose={onClose} />;
		default:
			unreachable(chip);
	}
}
